import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/Select';
import { useOrganizations } from '@/state/useOrganizations';

/**
 * Where a duplicated experiment is filed. The options are the websites of the account
 * the experiment belongs to: duplicating never leaves the account, and the API refuses
 * any other website.
 */

type DuplicateTargetParams = { experimentWebsiteId: number | null | undefined };

export const useDuplicateTarget = ({ experimentWebsiteId }: DuplicateTargetParams) => {
  const { organization } = useOrganizations();
  const [targetWebsiteId, setTargetWebsiteId] = useState<number | null>(experimentWebsiteId ?? null);

  const isMove = targetWebsiteId !== null && targetWebsiteId !== (experimentWebsiteId ?? null);
  const targetUrl = organization?.websites.find((site: { id: number }) => site.id === targetWebsiteId)?.url;

  return { targetWebsiteId, setTargetWebsiteId, isMove, targetUrl };
};

type DuplicateWebsiteSelectProps = {
  id: string;
  value: number | null;
  onChange: (websiteId: number) => void;
};

export const DuplicateWebsiteSelect = ({ id, value, onChange }: DuplicateWebsiteSelectProps) => {
  const { organization } = useOrganizations();
  const websites: { id: number; url: string }[] = organization?.websites ?? [];

  // One website is no choice to make.
  if (websites.length < 2) return null;

  return (
    <div className="grid gap-2">
      <Label htmlFor={id} className="micro-label">
        Website for the copy:
      </Label>
      <Select
        name={id}
        value={value === null ? null : String(value)}
        options={websites.map((site) => ({ value: site.id, label: site.url }))}
        onValueChange={(next) => onChange(+next)}
        className="border-line bg-field h-9 rounded-lg text-body"
      />
      <span className="supporting">
        Goals, targeting and redirect URLs are copied as they are, so check that they fit the new site. The editor URL
        and the run-length plan are cleared.
      </span>
    </div>
  );
};
